// AI Video Studio — Phase 2 caption presets + canvas renderer.
// One render function (`drawCaptionFrame`) is used BOTH for the live preview
// overlay and the burned-in export so what you see is exactly what you get.

export const CAPTION_PRESETS = [
  { key: 'bold_pop', name: 'Bold Pop', desc: 'Hormozi-style word pop', maxWords: 3, anim: 'pop', upper: true, swatch: ['#FFFFFF', '#FFE000'] },
  { key: 'karaoke', name: 'Karaoke', desc: 'Words highlight as spoken', maxWords: 6, anim: 'karaoke', swatch: ['#FFFFFF', '#FACC15'] },
  { key: 'tiktok_box', name: 'TikTok Box', desc: 'White box per word', maxWords: 1, anim: 'pop', box: true, upper: true, swatch: ['#FFFFFF', '#0A0A0A'] },
  { key: 'clean', name: 'Clean Minimal', desc: 'Simple white, gentle fade', maxWords: 4, anim: 'fade', swatch: ['#FFFFFF', '#E5E7EB'] },
  { key: 'subtitle', name: 'Subtitle Classic', desc: 'Translucent black bar', maxWords: 7, anim: 'none', bar: true, swatch: ['#000000', '#FFFFFF'] },
  { key: 'neon', name: 'Neon Glow', desc: 'Glowing outline pop', maxWords: 3, anim: 'pop', glow: true, upper: true, swatch: ['#22D3EE', '#F5D0FE'] },
];

export const getPreset = (key) => CAPTION_PRESETS.find((p) => p.key === key) || CAPTION_PRESETS[0];

// Group words into fixed-size chunks and compute each chunk's display window
// (a chunk stays on screen until the next one begins → no flicker in gaps).
export function buildGroups(words, maxWords) {
  if (!words || !words.length) return [];
  const groups = [];
  for (let i = 0; i < words.length; i += maxWords) {
    const slice = words.slice(i, i + maxWords);
    groups.push({ words: slice, start: slice[0].start, end: slice[slice.length - 1].end });
  }
  for (let i = 0; i < groups.length; i++) {
    groups[i].dispStart = groups[i].start;
    groups[i].dispEnd = i + 1 < groups.length ? groups[i + 1].start : groups[i].end + 1.0;
  }
  return groups;
}

// Re-align edited transcript text back onto the original word timestamps.
export function alignEdit(orig, text) {
  const toks = (text || '').trim().split(/\s+/).filter(Boolean);
  if (!orig || !orig.length) return toks.map((t, i) => ({ word: t, start: i * 0.4, end: i * 0.4 + 0.4 }));
  return toks.map((t, i) => {
    const ref = orig[Math.min(i, orig.length - 1)];
    return { word: t, start: ref.start, end: ref.end };
  });
}

const easeOut = (p) => 1 - Math.pow(1 - p, 3);

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function layout(ctx, items, maxWidth, space) {
  const lines = [];
  let cur = [];
  let curW = 0;
  for (const it of items) {
    const add = (cur.length ? space : 0) + it.w;
    if (cur.length && curW + add > maxWidth) {
      lines.push({ words: cur, width: curW });
      cur = [];
      curW = 0;
    }
    curW += (cur.length ? space : 0) + it.w;
    cur.push(it);
  }
  if (cur.length) lines.push({ words: cur, width: curW });
  return lines;
}

function wordState(groupWords, globalIdx, t) {
  // active = the word currently being spoken (last word whose start <= t)
  let active = -1;
  for (let i = 0; i < groupWords.length; i++) {
    if (t >= groupWords[i].start) active = i;
    else break;
  }
  if (globalIdx < active) return 'past';
  if (globalIdx === active) return 'active';
  return 'future';
}

function drawWord(ctx, item, centerX, y, fontSize, preset, state, scale) {
  ctx.save();
  ctx.translate(centerX, y);
  ctx.scale(scale, scale);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `${preset.bar ? '600' : '800'} ${fontSize}px Arial, sans-serif`;

  if (preset.box) {
    const padX = fontSize * 0.4;
    const padY = fontSize * 0.22;
    const bw = item.w + padX * 2;
    const bh = fontSize + padY * 2;
    ctx.fillStyle = '#FFFFFF';
    roundRect(ctx, -bw / 2, -bh / 2, bw, bh, fontSize * 0.2);
    ctx.fill();
    ctx.fillStyle = '#0A0A0A';
    ctx.fillText(item.text, 0, fontSize * 0.04);
    ctx.restore();
    return;
  }

  let fill = '#FFFFFF';
  if (preset.key === 'bold_pop') fill = state === 'active' ? '#FFE000' : '#FFFFFF';
  else if (preset.key === 'karaoke') fill = state === 'active' ? '#FACC15' : state === 'past' ? '#FFFFFF' : 'rgba(255,255,255,0.45)';
  else if (preset.key === 'neon') fill = state === 'active' ? '#F5D0FE' : '#22D3EE';

  if (!preset.bar && preset.key !== 'clean') {
    ctx.lineWidth = fontSize * 0.15;
    ctx.strokeStyle = '#000000';
    ctx.lineJoin = 'round';
    ctx.miterLimit = 2;
    ctx.strokeText(item.text, 0, fontSize * 0.04);
  }
  if (preset.glow) {
    ctx.shadowColor = state === 'active' ? '#E879F9' : '#22D3EE';
    ctx.shadowBlur = fontSize * 0.55;
  }
  ctx.fillStyle = fill;
  ctx.fillText(item.text, 0, fontSize * 0.04);
  ctx.restore();
}

// Draw the active caption for time `t` onto a canvas of W×H.
export function drawCaptionFrame(ctx, groups, t, preset, W, H) {
  if (!groups || !groups.length) return;
  const g = groups.find((gr) => t >= gr.dispStart && t < gr.dispEnd);
  if (!g) return;

  const fontSize = Math.round(H * (preset.bar ? 0.046 : preset.box ? 0.072 : 0.058));
  ctx.save();
  ctx.font = `${preset.bar ? '600' : '800'} ${fontSize}px Arial, sans-serif`;
  ctx.textBaseline = 'middle';
  const space = fontSize * 0.3;

  const dispWords = g.words.map((w) => {
    const text = (preset.upper ? w.word.toUpperCase() : w.word).trim();
    return { text, w: ctx.measureText(text).width };
  });

  const maxWidth = W * 0.86;
  const lines = layout(ctx, dispWords, maxWidth, space);
  const lineHeight = fontSize * 1.28;
  const bottomY = preset.box ? H * 0.5 : preset.bar ? H * 0.86 : H * 0.8;
  const firstY = bottomY - (lines.length - 1) * lineHeight;

  if (preset.key === 'clean') {
    ctx.globalAlpha = Math.min(1, Math.max(0, (t - g.dispStart) / 0.22));
  }

  if (preset.bar) {
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    for (let li = 0; li < lines.length; li++) {
      const ln = lines[li];
      const y = firstY + li * lineHeight;
      const x = (W - ln.width) / 2;
      roundRect(ctx, x - fontSize * 0.35, y - lineHeight / 2, ln.width + fontSize * 0.7, lineHeight, fontSize * 0.14);
      ctx.fill();
    }
  }

  let gi = 0;
  for (let li = 0; li < lines.length; li++) {
    const ln = lines[li];
    let x = (W - ln.width) / 2;
    const y = firstY + li * lineHeight;
    for (const item of ln.words) {
      const state = wordState(g.words, gi, t);
      let scale = 1;
      if (preset.anim === 'pop' && state === 'active') {
        const dt = Math.max(0, t - g.words[gi].start);
        scale = 1 + 0.16 * (1 - easeOut(Math.min(1, dt / 0.13)));
      }
      drawWord(ctx, item, x + item.w / 2, y, fontSize, preset, state, scale);
      x += item.w + space;
      gi++;
    }
  }
  ctx.restore();
}

// Cover-fit a video frame onto the canvas, letterboxed (contain) to match the
// Phase-1 export behavior (no surprise cropping).
export function drawVideoContain(ctx, video, W, H) {
  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, W, H);
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return;
  const scale = Math.min(W / vw, H / vh);
  const dw = vw * scale;
  const dh = vh * scale;
  ctx.drawImage(video, (W - dw) / 2, (H - dh) / 2, dw, dh);
}
